import { IS_SERVER } from '@/renderer/utils/ssr';

/**
 * Shared home-screen app-icon badge aggregator (issue #1347).
 *
 * The badge (via the Badging API `navigator.setAppBadge` / `clearAppBadge`) is a
 * single OS-level number, but we have multiple independent unread sources feeding
 * it — support-chat unreads and release-center unreads. If each source wrote the
 * badge directly they'd clobber each other (whichever updated last would win),
 * which is why support-chat notifications were getting masked by the release
 * center count and going unseen.
 *
 * Every unread source reports its current count here keyed by a stable id. The
 * badge is then set to the SUM of all sources, and only cleared once every source
 * is at zero. The Badging API is feature-detected and PWA/installed-only, so this
 * is a no-op (never throws) on browsers/contexts that don't support it.
 */

const counts = new Map<string, number>();

/**
 * The app-icon badge is a SINGLE per-app value that ANY same-origin context can
 * write — including the notifications service worker (sw/sw.ts), which sets it
 * from a background Web Push so a CLOSED or BACKGROUNDED PWA still badges when a
 * support-chat message arrives (#1694/#1719). A page that is NOT the foreground
 * window must therefore never write the badge: if it did, a backgrounded tab or a
 * still-open-but-unfocused PWA window would clobber the SW's push-set badge back
 * to its own (usually zero) live unread total — the exact "the notification
 * arrives but no badge appears" bug.
 *
 * "Foreground" here must mean the SAME thing the SW's push guard means by it, or
 * the two authorities fight: the SW skips badging only when a client is `focused`
 * (matchAll → `focused === true`), so the page may own the badge only when it is
 * that focused client — i.e. visible AND `document.hasFocus()`. Keying ownership
 * on visibility ALONE (#1719) left a gap: a visible-but-unfocused PWA whose live
 * unread is 0 (because the message was delivered by the SW push, not a live
 * websocket event) would clear the badge the SW had just set (#1738). So the page
 * owns the badge only while it is the focused window (reconciling to the true
 * unread total then, including clearing once everything is read), and leaves it to
 * the service worker otherwise.
 */
function pageOwnsBadge(): boolean {
  return (
    !IS_SERVER &&
    typeof document !== 'undefined' &&
    document.visibilityState === 'visible' &&
    typeof document.hasFocus === 'function' &&
    document.hasFocus()
  );
}

function applyBadge() {
  // Badging API only exists in secure, installed/PWA contexts. Guard it so we
  // never throw where it's unsupported (plain browser tabs, SSR, etc.).
  if (IS_SERVER || typeof navigator === 'undefined' || !('setAppBadge' in navigator)) {
    return;
  }

  // Don't let a hidden page fight the service worker's push-set badge (#1719).
  if (!pageOwnsBadge()) {
    return;
  }

  let total = 0;
  for (const value of counts.values()) {
    total += value;
  }

  if (total <= 0) {
    navigator.clearAppBadge?.();
  } else {
    navigator.setAppBadge?.(total);
  }
}

/** Report the current unread count for a given source and refresh the badge. */
export function setBadgeSource(key: string, count: number) {
  counts.set(key, Math.max(count, 0));
  applyBadge();
}

/** Remove a source (e.g. on unmount) and refresh the badge from the remainder. */
export function clearBadgeSource(key: string) {
  if (counts.delete(key)) {
    applyBadge();
  }
}

// Reconcile when this page becomes visible again — the moment it takes badge
// ownership back from the service worker. Without this, a page that was hidden
// (its live counts kept, the SW owning the badge) wouldn't refresh the badge to
// its own totals until the next count change. Registered once at module load;
// no-op on the server / where there's no document.
if (!IS_SERVER && typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      applyBadge();
    }
  });
}
